import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  CircularProgress,
  Alert,
  Fab,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Visibility,
  People,
  TrendingUp,
  Assignment,
  Check,
  Close,
  Email,
  Download
} from '@mui/icons-material';
import { Link, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { ro } from 'date-fns/locale';
import { 
  getAllPetitions, 
  getPendingPetitions, 
  deletePetition, 
  updatePetition, 
  approvePetition, 
  rejectPetition,
  getEmailStatistics,
  getUniqueEmails,
  getEmailsForExport
} from '../lib/supabase';
import toast from 'react-hot-toast';

const AdminDashboard = () => {
  const [petitions, setPetitions] = useState([]);
  const [pendingPetitions, setPendingPetitions] = useState([]);
  const [emailStats, setEmailStats] = useState([]);
  const [allEmails, setAllEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, petition: null });
  const [activeTab, setActiveTab] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [allPetitionsData, pendingData, emailStatsData, emailsData] = await Promise.all([
        getAllPetitions(),
        getPendingPetitions(),
        getEmailStatistics(),
        getUniqueEmails()
      ]);
      
      setPetitions(allPetitionsData);
      setPendingPetitions(pendingData);
      setEmailStats(emailStatsData);
      setAllEmails(emailsData);
    } catch (error) {
      toast.error('Eroare la încărcarea datelor');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePetition = async () => {
    try {
      await deletePetition(deleteDialog.petition.id);
      setPetitions(petitions.filter(p => p.id !== deleteDialog.petition.id));
      toast.success('Petiția a fost ștearsă');
    } catch (error) {
      toast.error('Eroare la ștergerea petiției');
    } finally {
      setDeleteDialog({ open: false, petition: null });
    }
  };

  const togglePetitionStatus = async (petition) => {
    try {
      const updated = await updatePetition(petition.id, {
        is_active: !petition.is_active
      });
      
      setPetitions(petitions.map(p => 
        p.id === petition.id ? { ...p, is_active: updated.is_active } : p
      ));
      
      toast.success(`Petiția a fost ${updated.is_active ? 'activată' : 'dezactivată'}`);
    } catch (error) {
      toast.error('Eroare la actualizarea statutului');
    }
  };

  const handleApprovePetition = async (petitionId) => {
    try {
      await approvePetition(petitionId);
      toast.success('Petiția a fost aprobată!');
      fetchData(); // Refresh data
    } catch (error) {
      toast.error('Eroare la aprobarea petiției');
    }
  };

  const handleRejectPetition = async (petitionId) => {
    try {
      await rejectPetition(petitionId);
      toast.success('Petiția a fost respinsă');
      fetchData(); // Refresh data
    } catch (error) {
      toast.error('Eroare la respingerea petiției');
    }
  };

  const exportEmails = async () => {
    try {
      const emailsData = await getEmailsForExport();
      
      // Creează CSV
      const csvContent = [
        ['Email', 'Nume', 'Petiție', 'Data Semnării', 'Mesaj'],
        ...emailsData.map(item => [
          item.email,
          item.name,
          item.petition_title,
          new Date(item.signed_at).toLocaleDateString('ro-RO'),
          item.message || ''
        ])
      ].map(row => row.join(',')).join('\n');

      // Download CSV
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `emails_psde_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success('Emailurile au fost exportate!');
    } catch (error) {
      toast.error('Eroare la exportul emailurilor');
    }
  };

  const totalSignatures = petitions.reduce((sum, p) => sum + (p.signatures?.[0]?.count || 0), 0);
  const activePetitions = petitions.filter(p => p.is_active).length;

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
      </Box>
    );
  }

  const TabPanel = ({ children, value, index, ...other }) => (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`admin-tabpanel-${index}`}
      aria-labelledby={`admin-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h3" component="h1" gutterBottom>
          Dashboard Administrativ
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          Gestionează petițiile și monitorizează activitatea platformei
        </Typography>
      </Box>

      {/* Tabs pentru navigare */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab label="Statistici & Petiții" />
          <Tab label={`Aprobare (${pendingPetitions.length})`} />
          <Tab label={`Emailuri (${allEmails.length})`} />
        </Tabs>
      </Box>

      {/* Tab Panel 0: Statistici & Petiții */}
      <TabPanel value={activeTab} index={0}>
        {/* Statistics Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Assignment sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
                <Box>
                  <Typography variant="h4" component="div">
                    {petitions.length}
                  </Typography>
                  <Typography color="text.secondary">
                    Total Petiții
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <TrendingUp sx={{ fontSize: 40, color: 'success.main', mr: 2 }} />
                <Box>
                  <Typography variant="h4" component="div">
                    {activePetitions}
                  </Typography>
                  <Typography color="text.secondary">
                    Petiții Active
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <People sx={{ fontSize: 40, color: 'secondary.main', mr: 2 }} />
                <Box>
                  <Typography variant="h4" component="div">
                    {totalSignatures}
                  </Typography>
                  <Typography color="text.secondary">
                    Total Semnături
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Visibility sx={{ fontSize: 40, color: 'warning.main', mr: 2 }} />
                <Box>
                  <Typography variant="h4" component="div">
                    {Math.round(totalSignatures / (petitions.length || 1))}
                  </Typography>
                  <Typography color="text.secondary">
                    Medie/Petiție
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Petitions Table */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5" component="h2">
              Toate Petițiile
            </Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              component={Link}
              to="/admin/create"
            >
              Petiție Nouă
            </Button>
          </Box>

          {petitions.length === 0 ? (
            <Alert severity="info">
              Nu există petiții create încă. Creează prima petiție!
            </Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Titlu</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Semnături</TableCell>
                    <TableCell>Categorie</TableCell>
                    <TableCell>Data Creării</TableCell>
                    <TableCell align="center">Acțiuni</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {petitions.map((petition) => (
                    <TableRow key={petition.id}>
                      <TableCell>
                        <Typography variant="subtitle2">
                          {petition.title}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={petition.is_active ? 'Activă' : 'Inactivă'}
                          color={petition.is_active ? 'success' : 'default'}
                          size="small"
                          onClick={() => togglePetitionStatus(petition)}
                          sx={{ cursor: 'pointer' }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <People fontSize="small" sx={{ mr: 0.5 }} />
                          {petition.signatures?.[0]?.count || 0}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {petition.category && (
                          <Chip label={petition.category} size="small" variant="outlined" />
                        )}
                      </TableCell>
                      <TableCell>
                        {format(new Date(petition.created_at), 'dd/MM/yyyy', { locale: ro })}
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          component={Link}
                          to={`/petitie/${petition.id}`}
                          target="_blank"
                        >
                          <Visibility />
                        </IconButton>
                        <IconButton
                          size="small"
                          component={Link}
                          to={`/admin/edit/${petition.id}`}
                        >
                          <Edit />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => setDeleteDialog({ open: true, petition })}
                        >
                          <Delete />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
      </TabPanel>

      {/* Tab Panel 1: Aprobare Petiții */}
      <TabPanel value={activeTab} index={1}>
        <Card>
          <CardContent>
            <Typography variant="h5" component="h2" gutterBottom>
              Petiții în Așteptarea Aprobării ({pendingPetitions.length})
            </Typography>
            
            {pendingPetitions.length === 0 ? (
              <Alert severity="info">
                Nu există petiții în așteptarea aprobării.
              </Alert>
            ) : (
              <Grid container spacing={3}>
                {pendingPetitions.map((petition) => (
                  <Grid item xs={12} key={petition.id}>
                    <Card variant="outlined" sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box sx={{ flexGrow: 1, mr: 2 }}>
                          <Typography variant="h6" gutterBottom>
                            {petition.title}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {petition.description}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                            <Typography variant="body2">
                              <strong>Submis de:</strong> {petition.submitted_by_name} ({petition.submitted_by_email})
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {format(new Date(petition.created_at), 'dd/MM/yyyy HH:mm', { locale: ro })}
                            </Typography>
                          </Box>
                          {petition.category && (
                            <Chip label={petition.category} size="small" variant="outlined" />
                          )}
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button
                            variant="contained"
                            color="success"
                            startIcon={<Check />}
                            onClick={() => handleApprovePetition(petition.id)}
                            size="small"
                          >
                            Aprobă
                          </Button>
                          <Button
                            variant="outlined"
                            color="error"
                            startIcon={<Close />}
                            onClick={() => handleRejectPetition(petition.id)}
                            size="small"
                          >
                            Respinge
                          </Button>
                          <IconButton
                            component={Link}
                            to={`/petitie/${petition.id}`}
                            target="_blank"
                            size="small"
                          >
                            <Visibility />
                          </IconButton>
                        </Box>
                      </Box>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </CardContent>
        </Card>
      </TabPanel>

      {/* Tab Panel 2: Emailuri */}
      <TabPanel value={activeTab} index={2}>
        <Grid container spacing={3}>
          {/* Statistici Email */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Statistici Emailuri
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Total emailuri unice
                  </Typography>
                  <Typography variant="h4" color="primary">
                    {allEmails.length}
                  </Typography>
                </Box>
                <Divider sx={{ my: 2 }} />
                {emailStats.map((stat, index) => (
                  <Box key={index} sx={{ mb: 2 }}>
                    <Typography variant="body2" gutterBottom>
                      <strong>{stat.petition_title}</strong>
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Semnături: {stat.total_signatures} | Emailuri: {stat.unique_emails} | Anonime: {stat.anonymous_count}
                    </Typography>
                  </Box>
                ))}
              </CardContent>
            </Card>
          </Grid>

          {/* Export și Listă Emailuri */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">
                    Baza de Date Emailuri
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<Download />}
                    onClick={exportEmails}
                    color="secondary"
                  >
                    Export CSV
                  </Button>
                </Box>
                
                <Alert severity="info" sx={{ mb: 2 }}>
                  Click pe "Export CSV" pentru a descărca toate emailurile cu detalii complete.
                </Alert>

                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Previzualizare ({allEmails.length} emailuri):
                </Typography>
                
                <Paper variant="outlined" sx={{ maxHeight: 300, overflow: 'auto' }}>
                  <List dense>
                    {allEmails.slice(0, 10).map((email, index) => (
                      <ListItem key={index}>
                        <ListItemIcon>
                          <Email fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary={email} />
                      </ListItem>
                    ))}
                    {allEmails.length > 10 && (
                      <ListItem>
                        <ListItemText 
                          primary={`... și încă ${allEmails.length - 10} emailuri`}
                          sx={{ fontStyle: 'italic', color: 'text.secondary' }}
                        />
                      </ListItem>
                    )}
                  </List>
                </Paper>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      {/* Floating Action Button */}
      <Fab
        color="primary"
        aria-label="add petition"
        sx={{ position: 'fixed', bottom: 16, right: 16 }}
        component={Link}
        to="/admin/create"
      >
        <Add />
      </Fab>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, petition: null })}
      >
        <DialogTitle>Confirmare Ștergere</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Ești sigur că vrei să ștergi petiția "{deleteDialog.petition?.title}"? 
            Această acțiune nu poate fi anulată și toate semnăturile vor fi pierdute.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, petition: null })}>
            Anulează
          </Button>
          <Button onClick={handleDeletePetition} color="error" variant="contained">
            Șterge
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AdminDashboard;
